<template>
  <v-card 
    class="premium-profile-card"
    :class="{ 'card-hover': !isMobile }"
    elevation="0"
    @mouseenter="handleHover(true)"
    @mouseleave="handleHover(false)"
  >
    <!-- Gradient Background Overlay -->
    <div class="gradient-overlay"></div>
    
    <!-- Animated Background Pattern -->
    <div class="bg-pattern">
      <div class="pattern-dot" v-for="i in 20" :key="i" :style="getDotStyle(i)"></div>
    </div>
    
    <!-- Main Content Container -->
    <div class="profile-content">
      <!-- Profile Avatar with Floating Animation -->
      <div class="avatar-container" :class="{ 'avatar-floating': isHovered }">
        <div class="avatar-glow"></div>
        <div 
          class="avatar-wrapper"
          :style="avatarStyles"
          @click="goToProfile"
        >
          <img
            v-if="profile.midThumbnail || profile.image"
            :src="profile.midThumbnail ? profile.midThumbnail : profile.image"
            :alt="profile.name || 'Profile'"
            class="avatar-img"
          />
          <div v-else class="avatar-placeholder">
            <v-icon size="40" color="rgba(255,255,255,0.7)">mdi-account</v-icon>
          </div>
          
          <!-- Status Indicator -->
          <!-- <div class="status-indicator online"></div> -->
          
          <!-- Hover Overlay -->
          <div class="avatar-overlay">
            <v-icon color="white" size="24">mdi-camera</v-icon>
          </div>
        </div>
      </div>

      <!-- Profile Information -->
      <div class="profile-info">
        <!-- Name with Typing Animation -->
        <h2 
          class="profile-name"
          v-if="profile.name"
          @click="goToProfile"
        >
          {{ profile.name }}
          <v-icon 
            color="primary" 
            size="16" 
            class="verified-badge ml-1"
            v-if="profile.email_verified && profile.phone_verified"
          >
            mdi-check-decagram
          </v-icon>
        </h2>
        
        <!-- Location with Icon Animation -->
        <div 
          class="location-info"
          v-if="profile.address"
          :class="{ 'location-pulse': isHovered }"
        >
          <div class="location-icon-wrapper">
            <v-icon color="primary" size="16">mdi-map-marker</v-icon>
          </div>
          <span class="location-text">
            {{ profile.address }}{{ profile.address2 ? ', ' + profile.address2 : '' }}, {{ profile.city }}
          </span>
        </div>

        <!-- Stats Section -->
        <!-- <div class="stats-container" v-if="showStats">
          <div class="stat-item" @click="goToWorkHours">
            <div class="stat-icon">
              <v-icon color="primary" size="18">mdi-clock-outline</v-icon>
            </div>
            <div class="stat-content">
              <span class="stat-number">{{ Math.round((profile.volunteered_hours || 0) / 60) }}</span>
              <span class="stat-label">Hours</span>
            </div>
          </div>
          
          <div class="stat-divider"></div>
          
          <div class="stat-item" @click="followers">
            <div class="stat-icon">
              <v-icon color="primary" size="18">mdi-account-group</v-icon>
            </div>
            <div class="stat-content">
              <span class="stat-number">{{ (profile.followers && profile.followers.length) || 0 }}</span>
              <span class="stat-label">Following</span>
            </div>
          </div>
        </div> -->

        <!-- Action Buttons -->
        <div class="action-buttons">
          <v-btn
            class="action-btn primary-btn"
            rounded
            elevation="0"
            @click="goToProfile"
          >
            <v-icon left size="18">mdi-account</v-icon>
            View Profile
          </v-btn>
          
          <!-- <v-btn
            class="action-btn secondary-btn"
            rounded
            outlined
            elevation="0"
            @click="goToSetting"
            v-if="getCompletedInfo"
          >
            <v-icon left size="18">mdi-cog</v-icon>
            Settings
          </v-btn> -->
        </div>
      </div>
    </div>

    <!-- Completion Progress Bar -->
    <!-- <div class="progress-container" v-if="completionPercentage < 100">
      <div class="progress-info">
        <span class="progress-label">Profile Completion</span>
        <span class="progress-percentage">{{ completionPercentage }}%</span>
      </div>
      <div class="progress-bar">
        <div 
          class="progress-fill" 
          :style="{ width: completionPercentage + '%' }"
        ></div>
      </div>
    </div> -->

    <!-- Floating Action Button -->
    <!-- <v-btn
      fab
      small
      color="primary"
      class="floating-btn"
      elevation="3"
      @click="goToSetting"
      v-if="getCompletedInfo"
    >
      <v-icon>mdi-pencil</v-icon>
    </v-btn> -->
  </v-card>
</template>

<script>
import { mapActions } from "vuex";

export default {
  components: {},
  props: {
    profile: {
      type: Object,
      default: () => ({})
    },
    onClicked: Function,
    showStats: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      placeholderImage: require("@/assets/default.jpg"),
      profileColour: {},
      avatarBorderColor: "none",
      avatarBorderStyle: "none",
      isHovered: false,
      isMobile: false
    };
  },
  computed: {
    getCompletedInfo() {
      if (!this.profile._id) return "";
      const missing = [];
      
      if (!this.profile.image) missing.push("Image");
      if (!this.profile.about_me) missing.push("About Me");
      if (!this.profile.phone_verified) missing.push("Phone Verification");
      if (!this.profile.email_verified) missing.push("Email Verification");
      if (!this.profile.interests || this.profile.interests.length === 0) missing.push("Interests");
      if (!this.profile.causes || this.profile.causes.length === 0) missing.push("Causes");
      if (!this.profile.availabilities || this.profile.availabilities.length === 0) missing.push("Availability");
      if (!this.profile.phone) missing.push("Phone");
      if (!this.profile.address) missing.push("Address");
      if (!this.profile.city) missing.push("City");
      if (!this.profile.state) missing.push("State");
      if (!this.profile.zip) missing.push("Zip Code");

      return missing.join(", ");
    },
    
    completionPercentage() {
      const totalFields = 11;
      const missingCount = this.getCompletedInfo.split(", ").filter(item => item.trim()).length;
      return Math.round(((totalFields - missingCount) / totalFields) * 100);
    },
    
    avatarStyles() {
      return {
        borderColor: this.avatarBorderColor !== "none" ? this.avatarBorderColor : "transparent",
        borderWidth: this.avatarBorderColor !== "none" ? "3px" : "0px",
        borderStyle: this.avatarBorderStyle
      };
    }
  },
  methods: {
    ...mapActions({
      volunteerProfile: "volunteer/volunteerProfile"
    }),
    
    handleHover(hovered) {
      if (!this.isMobile) {
        this.isHovered = hovered;
      }
    },
    
    getDotStyle() {
      const size = Math.random() * 4 + 2;
      const left = Math.random() * 100;
      const top = Math.random() * 100;
      const delay = Math.random() * 4;
      
      return {
        width: `${size}px`,
        height: `${size}px`,
        left: `${left}%`,
        top: `${top}%`,
        animationDelay: `${delay}s`
      };
    },
    
    goToProfile() {
      this.$router.push({ name: "company-profile" });
    },
    
    goToSetting() {
      if (!this.profile.email_verified || !this.profile.phone_verified) {
        this.$router.push({ name: "settings" });
      } else {
        this.$router.push({
          name: "volunteer-profile-about",
          params: { id: this.profile._id }
        });
      }
    },
    
    goToWorkHours() {
      this.$router.push({ name: "volunteer-hours" });
    },
    
    followers() {
      this.$router.push({ name: "volunteers" });
    },
    
    loadColours() {
      this.volunteerProfile()
        .then(res => {
          this.profileColour = res;
          this.avatarBorderColor = this.profileColour.avatarColour || "none";
          this.avatarBorderStyle = this.profileColour.avatarColour ? "solid" : "none";
        })
        .catch(error => {
          console.log(error);
        });
    },
    
    checkMobile() {
      this.isMobile = this.$vuetify.breakpoint.smAndDown;
    }
  },
  
  mounted() {
    this.loadColours();
    this.checkMobile();
  }
};
</script>

<style scoped>
.premium-profile-card {
  position: relative;
  background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 24px !important;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  /* border: 1px solid rgba(0, 0, 0, 0.04); */
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  backdrop-filter: blur(10px);
}

.card-hover:hover {
  transform: translateY(-8px);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
}

.gradient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 150px;
  background: linear-gradient(135deg, #084468, #0a5b8a);
  opacity: 0.9;
  z-index: 1;
}

.bg-pattern {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 150px;
  z-index: 2;
  overflow: hidden;
}

.pattern-dot {
  position: absolute;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  animation: float 6s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.3; }
  50% { transform: translateY(-20px) rotate(180deg); opacity: 0.8; }
}

.profile-content {
  position: relative;
  z-index: 3;
  padding: 80px 24px 24px;
  text-align: center;
}

.avatar-container {
  position: relative;
  display: inline-block;
  margin-bottom: 20px;
  transition: transform 0.3s ease;
}

.avatar-floating {
  animation: avatarFloat 3s ease-in-out infinite;
}

@keyframes avatarFloat {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
}

.avatar-glow {
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  background: linear-gradient(135deg, #084468, #0a5b8a);
  border-radius: 50%;
  opacity: 0;
  transition: opacity 0.3s ease;
  animation: pulse 2s infinite;
  z-index: -1;
}

.avatar-container:hover .avatar-glow {
  opacity: 0.3;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.avatar-wrapper {
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  background: linear-gradient(145deg, #f0f4f8, #d1e7dd);
}

.avatar-wrapper:hover {
  transform: scale(1.05);
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.avatar-wrapper:hover .avatar-img {
  transform: scale(1.1);
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #084468, #0a5b8a);
}

.status-indicator {
  position: absolute;
  bottom: 8px;
  right: 8px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 3px solid white;
}

.status-indicator.online {
  background: #10b981;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.3);
  animation: statusPulse 2s infinite;
}

@keyframes statusPulse {
  0% { box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.3); }
  50% { box-shadow: 0 0 0 6px rgba(16, 185, 129, 0.1); }
  100% { box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.3); }
}

.avatar-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.avatar-wrapper:hover .avatar-overlay {
  opacity: 1;
}

.profile-info {
  text-align: center;
}

.profile-name {
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
  cursor: pointer;
  transition: color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.profile-name:hover {
  color: #0a5b8a;
}

.verified-badge {
  animation: sparkle 2s infinite;
}

@keyframes sparkle {
  0%, 100% { transform: scale(1) rotate(0deg); }
  50% { transform: scale(1.1) rotate(10deg); }
}

.location-info {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  color: #6b7280;
  font-size: 14px;
  transition: transform 0.3s ease;
}

.location-pulse {
  animation: locationPulse 1.5s ease-in-out;
}

@keyframes locationPulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.02); }
  100% { transform: scale(1); }
}

.location-icon-wrapper {
  margin-right: 8px;
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-4px); }
  60% { transform: translateY(-2px); }
}

.stats-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 24px 0;
  padding: 16px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 16px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.stat-item {
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: transform 0.2s ease;
  padding: 8px 16px;
  border-radius: 12px;
}

.stat-item:hover {
  transform: scale(1.05);
  background: rgba(102, 126, 234, 0.1);
}

.stat-icon {
  margin-right: 8px;
}

.stat-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.stat-number {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1;
}

.stat-label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

.stat-divider {
  width: 1px;
  height: 30px;
  background: linear-gradient(to bottom, transparent, #e5e7eb, transparent);
  margin: 0 16px;
}

.action-buttons {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

.action-btn {
  font-weight: 600 !important;
  text-transform: none !important;
  padding: 0 24px !important;
  height: 44px !important;
  transition: all 0.3s ease !important;
}

.primary-btn {
   background: linear-gradient(135deg, #084468, #0a5b8a) !important;
  color: white !important;
}

.primary-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3) !important;
}

.secondary-btn {
  border: 2px solid #667eea !important;
  color: #667eea !important;
}

.secondary-btn:hover {
  background: #667eea !important;
  color: white !important;
  transform: translateY(-2px);
}

.progress-container {
  margin: 20px 24px 0;
  padding-top: 20px;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.progress-label {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

.progress-percentage {
  font-size: 14px;
  color: #667eea;
  font-weight: 700;
}

.progress-bar {
  height: 6px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 3px;
  transition: width 0.8s ease;
  position: relative;
}

.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

/* .floating-btn {
  position: absolute !important;
  top: 16px;
  right: 16px;
  z-index: 4;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  animation: floatingPulse 3s infinite;
} */

@keyframes floatingPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.floating-btn:hover {
  transform: scale(1.1) !important;
}

/* Mobile Responsiveness */
@media (max-width: 600px) {
  .profile-content {
    padding: 60px 16px 16px;
  }
  
  .action-buttons {
    flex-direction: column;
    align-items: stretch;
  }
  
  .action-btn {
    width: 100% !important;
  }
  
  .stats-container {
    flex-direction: column;
    gap: 16px;
  }
  
  .stat-divider {
    width: 80%;
    height: 1px;
    margin: 0;
  }
}

/* Dark theme support */
.theme--dark .premium-profile-card {
  background: linear-gradient(145deg, #1f2937 0%, #111827 100%);
  border-color: rgba(255, 255, 255, 0.1);
}

.theme--dark .profile-name {
  color: #f9fafb;
}

.theme--dark .location-info {
  color: #d1d5db;
}

.theme--dark .stat-number {
  color: #f9fafb;
}

.theme--dark .stat-label {
  color: #9ca3af;
}

.theme--dark .progress-label {
  color: #d1d5db;
}
</style>